export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              Time is money. A 24-hour delay on a pour can cost thousands in lost productivity, idle equipment, and frustrated crews. In construction, speed isn't just about working faster—it's about working smarter.
            </p>

            <p>
              At <strong>Dinari Designs</strong>, we understand that your crew can't wait days for drawings. That's why we've built our business around <strong>rapid turnaround</strong> without sacrificing quality. Here's how professional CAD layouts keep your project moving:
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-time-line text-white text-xl"></i>
                </span>
                The Cost of Waiting
              </h2>
              <p className="text-gray-700 mb-0">
                Every day your crew spends waiting for drawings is a day they're not building. Idle time means paying workers to stand around, equipment sitting unused, and project schedules slipping. For a typical Vancouver construction project, a single day of delay can cost <strong>$5,000-$10,000</strong> or more.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-flashlight-line text-white text-xl"></i>
                </span>
                Our 24-48 Hour Turnaround Commitment
              </h2>
              <p className="text-gray-700 mb-0">
                We commit to providing <strong>quotes within 24-48 hours</strong> and delivering final drawings on a timeline that matches your construction schedule. This isn't just a promise—it's how we operate. Our streamlined <strong>AutoCAD workflow</strong> and dedicated team ensure that your project never waits on us.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hammer-line text-white text-xl"></i>
                </span>
                Ready-to-Build Drawings
              </h2>
              <p className="text-gray-700 mb-0">
                Professional CAD layouts mean your crew arrives on site with clear, detailed drawings in hand. No more standing around trying to interpret vague sketches or waiting for the foreman to "figure it out." They start hammering immediately, which means faster progress and better morale.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-edit-line text-white text-xl"></i>
                </span>
                Fast Revisions When Plans Change
              </h2>
              <p className="text-gray-700 mb-0">
                Construction is dynamic. Plans change, site conditions evolve, and sometimes you need a quick revision. Our expertise in <strong>Bluebeam Revu</strong> allows us to mark up changes and process revisions rapidly—often within hours. This agility keeps your project on track even when the unexpected happens.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-calendar-check-line text-white text-xl"></i>
                </span>
                Coordinated Scheduling
              </h2>
              <p className="text-gray-700 mb-0">
                We work with your project schedule, not against it. Need drawings for a Monday morning pour? We'll have them ready Friday afternoon. Planning a multi-phase project? We'll coordinate our deliverables to match your construction sequencing. Your timeline is our timeline.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Your crew deserves drawings that are ready when they are. With our <strong>fast turnaround professional CAD drafting</strong>, you'll never lose another day to waiting.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
